import { GraduationCap, Home, Users, Building } from 'lucide-react';
import FeatureDetailPage from '../../components/marketing/FeatureDetailPage';
import { useLanguage } from '../../contexts/LanguageContext';

const TutorsUseCase = () => {
  const { tr } = useLanguage();
  const relatedFeatures = [
    { icon: GraduationCap, iconColor: 'bg-purple-100 text-purple-600', titleKey: 'useCaseClassroomTitle', descKey: 'useCaseClassroomDesc', link: '/use-cases/classroom-teachers' },
    { icon: Home, iconColor: 'bg-teal-100 text-teal-600', titleKey: 'useCaseHomeschoolTitle', descKey: 'useCaseHomeschoolDesc', link: '/use-cases/homeschool' },
    { icon: Building, iconColor: 'bg-blue-100 text-blue-600', titleKey: 'useCaseSchoolsTitle', descKey: 'useCaseSchoolsDesc', link: '/use-cases/schools' }
  ];

  const workflows = [
    {
      title: tr('Plan every session in minutes', 'Planifica cada sesión en minutos'),
      desc: tr('Tell the AI Assistant the subject, grade level and topic, and get a complete session plan with objectives, guided practice and independent work. Reuse plans across students with similar needs, or adapt them per student in seconds.', 'Dile al Asistente de IA la materia, el grado y el tema, y obtén un plan de sesión completo con objetivos, práctica guiada y trabajo independiente. Reutiliza planes entre estudiantes con necesidades similares o adáptalos por estudiante en segundos.')
    },
    {
      title: tr('Track progress between sessions', 'Haz seguimiento del progreso entre sesiones'),
      desc: tr("Every quiz score, rubric result and attendance record lives in one student profile. Open the Student 360° view before a session to see exactly where you left off and which skills still need reinforcement — no more digging through notebooks.", 'Cada puntuación de quiz, resultado de rúbrica y registro de asistencia vive en un solo perfil del estudiante. Abre la vista Estudiante 360° antes de una sesión para ver exactamente dónde quedaron y qué destrezas aún necesitan refuerzo, sin buscar en cuadernos.')
    },
    {
      title: tr('Create practice materials on demand', 'Crea materiales de práctica al instante'),
      desc: tr('Generate worksheets, quizzes with answer keys, flash activities and adaptive learning paths matched to each student\u2019s level — including ESL-adapted material for English learners aligned with WIDA and Puerto Rico standards.', 'Genera hojas de trabajo, quizzes con claves de respuestas, actividades rápidas y rutas de aprendizaje adaptativo según el nivel de cada estudiante, incluyendo material adaptado a ESL para estudiantes de inglés alineado con los estándares WIDA y de Puerto Rico.')
    },
    {
      title: tr('Keep parents in the loop', 'Mantén a los padres informados'),
      desc: tr('Share progress through parent portals and personalized email updates generated from real session data — grades, attendance and what was covered — so parents always see the value of your tutoring.', 'Comparte el progreso mediante portales para padres y correos personalizados generados a partir de datos reales de las sesiones: calificaciones, asistencia y lo que se cubrió, para que los padres siempre vean el valor de tus tutorías.')
    }
  ];

  const faqs = [
    {
      q: tr('Can I manage students across different grade levels and subjects?', '¿Puedo manejar estudiantes de diferentes grados y materias?'),
      a: tr('Yes. Create a separate class for each student or small group, each with its own grade level, subject, schedule, gradebook and materials. TeacherHubPro keeps everything organized per student so switching between a 3rd-grade math student and a 10th-grade English student takes one click.', 'Sí. Crea una clase separada para cada estudiante o grupo pequeño, cada una con su propio grado, materia, horario, registro de notas y materiales. TeacherHubPro mantiene todo organizado por estudiante, así cambiar entre un estudiante de matemáticas de 3er grado y uno de inglés de 10mo toma un clic.')
    },
    {
      q: tr('Does it work for online tutoring?', '¿Funciona para tutorías en línea?'),
      a: tr('Absolutely. Share worksheets and assignments by link, host live Play to Learn quiz games students join from any device, and send adaptive learning paths students complete on their own between sessions.', 'Por supuesto. Comparte hojas de trabajo y asignaciones por enlace, organiza juegos de quiz en vivo con Play to Learn a los que los estudiantes se unen desde cualquier dispositivo, y envía rutas de aprendizaje adaptativo que los estudiantes completan por su cuenta entre sesiones.')
    },
    {
      q: tr('How does the free trial work?', '¿Cómo funciona la prueba gratis?'),
      a: tr('Sign up and get full access to every feature — AI lesson planning, gradebook, attendance, rubrics and games — with no credit card required. Keep everything you create if you subscribe later.', 'Regístrate y obtén acceso completo a todas las funciones —planificación con IA, registro de notas, asistencia, rúbricas y juegos— sin tarjeta de crédito. Conserva todo lo que crees si te suscribes después.')
    },
    {
      q: tr('Can I support students who are learning English?', '¿Puedo apoyar a estudiantes que están aprendiendo inglés?'),
      a: tr('Yes. Turn on ESL Support in any AI tool to generate materials with simplified language, key vocabulary with Spanish cognates and sentence frames, aligned with WIDA and Puerto Rico Department of Education standards.', 'Sí. Activa el Apoyo ESL en cualquier herramienta de IA para generar materiales con lenguaje simplificado, vocabulario clave con cognados en español y marcos de oraciones, alineados con los estándares WIDA y del Departamento de Educación de Puerto Rico.')
    }
  ];

  return (
    <FeatureDetailPage
      featureKey="Tutors"
      icon={Users}
      iconColor="bg-amber-100 text-amber-600"
      heroImage="https://static.prod-images.emergentagent.com/jobs/9651e3ca-5bf4-4d9b-b045-1d41a1fb3908/images/d4f2f4c611311c9d69cce2eb99dad71a22876cd80ad8a892759c1344361823b2.png"
      relatedFeatures={relatedFeatures}
    >
      {/* Tutor workflows */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 sm:mb-10 text-center">
            {tr('Built for the way tutors actually work', 'Hecho para la forma en que realmente trabajan los tutores')}
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {workflows.map((w, i) => (
              <div key={i} className="bg-amber-50/60 border border-amber-100 rounded-xl p-5 sm:p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{w.title}</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">
            {tr('Frequently asked questions from tutors', 'Preguntas frecuentes de tutores')}
          </h2>
          <div className="space-y-5">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{f.q}</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </FeatureDetailPage>
  );
};

export default TutorsUseCase;
